<template>
	<div>
		<div
			ref="host"
			class="codegen-diff-host"
			:style="`height: ${height}px; overflow: auto; border: 1px solid #ced4da; border-radius: 4px; font-size: 13px;`"
		></div>
	</div>
</template>

<script>
// Diff visuel type VS Code (accept/reject par hunk) entre un code de référence
// ("original", document A) et un code éditable ("value", document B) — voir
// saas_front/TODO_CODEGEN_CHAT.md section 2. S'appuie sur @codemirror/merge
// (unifiedMergeView), qui affiche un seul éditeur avec les blocs modifiés
// surlignés et des boutons accepter/rejeter intégrés nativement par bloc.
import { EditorView, keymap, lineNumbers } from "@codemirror/view";
import { EditorState } from "@codemirror/state";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { javascript } from "@codemirror/lang-javascript";
import { html } from "@codemirror/lang-html";
import { vue } from "@codemirror/lang-vue";
import { syntaxHighlighting, defaultHighlightStyle, syntaxTree } from "@codemirror/language";
import { linter, lintGutter } from "@codemirror/lint";
import { unifiedMergeView, acceptChunk, rejectChunk, getChunks, getOriginalDoc } from "@codemirror/merge";

// Linter générique basé sur l'arbre de syntaxe Lezer (construit par le langage
// actif : @codemirror/lang-javascript, lang-html ou lang-vue, cf prop
// `language`) : Lezer produit des noeuds "error" quand il ne parvient pas à
// analyser une portion du texte (accolade/balise non fermée, token inattendu,
// ...). On parcourt l'arbre à chaque mise à jour et on remonte un diagnostic
// pour chaque noeud d'erreur trouvé — fonctionne quel que soit le langage, sans
// code spécifique par langage (contrairement à `new Function(...)`, qui ne
// marche que pour du JS). Limite connue : ne détecte que les erreurs de
// syntaxe brutes (arbre non analysable), pas les erreurs sémantiques (variable
// non définie, etc.).
function syntaxErrorLinter(view) {
	var diagnostics = [];
	syntaxTree(view.state).iterate({
		enter: (node) => {
			if (node.type.isError) {
				diagnostics.push({
					from: node.from,
					to: node.to > node.from ? node.to : Math.min(node.from + 1, view.state.doc.length),
					severity: "error",
					message: "Erreur de syntaxe"
				});
			}
		}
	});
	return diagnostics;
}

export default {
	name: "codemirror_unified_diff",
	props: {
		// Code final éditable (document B) — v-model
		value: {
			type: String,
			default: ""
		},
		// Code de référence (document A) contre lequel le diff est affiché
		original: {
			type: String,
			default: ""
		},
		height: {
			type: Number,
			default: 320
		},
		readOnly: {
			type: Boolean,
			default: false
		},
		// Marquage fin (au caractère/mot) des portions insérées/supprimées à
		// l'intérieur des lignes changées, en plus du fond de ligne coloré.
		// Peut être désactivé si jugé trop bruyant (ex. quand tout le fichier
		// est un seul gros chunk, comme lors d'une première génération sans
		// code de référence).
		highlightChanges: {
			type: Boolean,
			default: false
		},
		// Langage utilisé pour la coloration syntaxique du code affiché : le code
		// généré n'est pas toujours du JS (ex. tableaux de bord "texte" qui génèrent
		// du HTML, ou "composant" qui génère un fichier Vue SFC complet, cf
		// saas_front/TODO_CODEGEN_CHAT.md section 10). Valeurs supportées :
		// "javascript" (défaut), "html" ou "vue".
		language: {
			type: String,
			default: "javascript"
		}
	},
	data() {
		return {
			_value: this.value,
			_original: this.original
		};
	},
	watch: {
		value(newValue) {
			// Ne reconstruire que si le changement vient de l'extérieur (nouvelle
			// génération, chargement d'un chat sauvegardé) — pas de nos propres
			// émissions "input" (accept/reject/édition dans l'éditeur lui-même),
			// sinon on perdrait l'état des hunks et la position du curseur à
			// chaque frappe. Même convention que ace_editor.vue.
			if (newValue !== this._value)
				this.rebuild();
		},
		original(newValue) {
			// Idem pour "original" (document A) : un vrai changement externe (nouvelle
			// génération) doit reconstruire la vue, mais pas nos propres émissions
			// "update:original" (accept/reject qui mettent à jour le parent en
			// continu — voir updateListener plus bas).
			if (newValue !== this._original)
				this.rebuild();
		}
	},
	methods: {
		rebuild() {
			if (this.view)
				this.view.destroy();

			this._value = this.value;
			this._original = this.original;

			var languageExtension = javascript();
			if (this.language === "html")
				languageExtension = html();
			else if (this.language === "vue")
				languageExtension = vue();

			var extensions = [
				history(),
				keymap.of([...defaultKeymap, ...historyKeymap]),
				lineNumbers(),
				languageExtension,
				syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
				linter(syntaxErrorLinter),
				lintGutter(),
				EditorView.lineWrapping,
				unifiedMergeView({
					original: this.original || "",
					mergeControls: true,
					gutter: true,
					highlightChanges: this.highlightChanges
				}),
				EditorView.updateListener.of((update) => {
					if (update.docChanged) {
						this._value = update.state.doc.toString();
						this.$emit("input", this._value);
					}

					// Accepter/rejeter un hunk (y compris via les boutons natifs
					// affichés par unifiedMergeView) ne change pas forcément le
					// document B (cas d'un chunk purement inséré : le texte est déjà
					// bon, seul le doc "original" interne est mis à jour pour ne
					// plus signaler ce hunk comme modifié). Cet état ne vit que dans
					// this.view.state : on le fait remonter au parent en continu via
					// "update:original" pour qu'il survive à une éventuelle recréation
					// du composant/de la vue (ex. fermeture/réouverture de la modale)
					// au lieu de se re-proposer les hunks déjà tranchés.
					var orig_doc = getOriginalDoc(update.state).toString();
					if (orig_doc !== this._original) {
						this._original = orig_doc;
						this.$emit("update:original", this._original);
					}
				})
			];

			if (this.readOnly)
				extensions.push(EditorView.editable.of(false));

			this.view = new EditorView({
				parent: this.$refs.host,
				state: EditorState.create({
					doc: this.value || "",
					extensions: extensions
				})
			});
		},
		acceptAll() {
			if (!this.view)
				return;
			var info = getChunks(this.view.state);
			var guard = 0;
			while (info && info.chunks && info.chunks.length > 0 && guard < 1000) {
				acceptChunk(this.view, info.chunks[0].fromB);
				info = getChunks(this.view.state);
				guard++;
			}
		},
		rejectAll() {
			if (!this.view)
				return;
			var info = getChunks(this.view.state);
			var guard = 0;
			while (info && info.chunks && info.chunks.length > 0 && guard < 1000) {
				rejectChunk(this.view, info.chunks[0].fromB);
				info = getChunks(this.view.state);
				guard++;
			}
		},
		hasPendingChunks() {
			if (!this.view)
				return false;
			var info = getChunks(this.view.state);
			return !!(info && info.chunks && info.chunks.length > 0);
		},
		getValue() {
			return this.view ? this.view.state.doc.toString() : this._value;
		}
	},
	mounted() {
		this.rebuild();
	},
	beforeDestroy() {
		if (this.view)
			this.view.destroy();
	}
};
</script>

<style scoped>
.codegen-diff-host >>> .cm-editor {
	height: 100%;
}
</style>
